import React, { useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { StaticRouter } from "react-router-dom/server";
import { IntlProvider, useIntl } from "react-intl";
import { AnyPrice } from "@ct-react/core";
import { castBookingOption } from "@ct-react/calendar";
import { defaultLocale, LocaleProvider, useLocaleContext, useLocaleFormatter, useLocaleUrl } from "@ct-react/locale";
import { BOOKING_SUGGESTION_FRAGMENT } from "@shared/gql/fragments";
import { getIntlMessages } from "../../i18n";
import { bookingTranslations } from "../../i18n/sharable-defs";
import { Ratio } from "../../models/medias";
import SearchMarker from "./search-marker";
import Summary from "../../components/cards/summary";
import { MapMarker, MapPopup } from "./index";

const SEARCHED_ITEM_GQL_DATA = gql`
  ${BOOKING_SUGGESTION_FRAGMENT}
  query SearchItem($where: AnyArticleFilterInput!) {
    search(where: $where, first: 1) {
      edges {
        node {
          ... on RentalAccommodationArticle {
            id slug title { value } images { main aspectRatio assets { url } } merchant { slug } }
          }
        accommodationBookingSuggestion { ...BookingSuggestionFields }
      }
    }
  }
`;

type AsyncPopupMarkerProps = {
  id: string;
  coordinates: any;
  priceOnDemand?: boolean;
  price?: AnyPrice;
  contextQuery?: any;
}
const AsyncPopupMarker = (
  {
    id,
    coordinates,
    priceOnDemand,
    price,
    contextQuery
  }: AsyncPopupMarkerProps) => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { currentUrl } = useLocaleUrl();
  const { print } = useLocaleFormatter();

  const [ getSummary, { data } ] = useLazyQuery(SEARCHED_ITEM_GQL_DATA, { ssr: false });

  const onceClick = () => getSummary({
    ssr: false,
    variables: {
      where: {
        rentalAccommodation: {
          ids: [ id ],
          ...(!!contextQuery) && contextQuery.rentalAccommodation
        }
      }
    }
  });

  const iconRendering = useMemo(() => {
    let label: string | undefined = undefined;
    if (priceOnDemand) label = intl.formatMessage(bookingTranslations.priceOnDemand);
    if (!!price) label = print.price(price);
    return (<SearchMarker label={label} />)
  }, [ id, priceOnDemand, price ]);

  const popupRendering = useMemo(() => {
    if (!data?.search.edges[0]) return undefined;
    const edge = data.search.edges[0];
    return (
      <MapPopup className="rls-map-popup">
        <LocaleProvider locale={locale}>
          <IntlProvider locale={locale.code}
                        defaultLocale={defaultLocale.code}
                        messages={getIntlMessages(locale)}>
            <StaticRouter location={currentUrl}>
              <Summary {...edge.node}
                       bookingSuggestion={castBookingOption(edge.accommodationBookingSuggestion)}
                       ratio={Ratio.SIXTEEN_NINE}
                       view="map" />
            </StaticRouter>
          </IntlProvider>
        </LocaleProvider>
      </MapPopup>);
  }, [ data ]);

  return <MapMarker position={coordinates}
                    icon={iconRendering}
                    asyncPopupOnceClick={onceClick}>{popupRendering}</MapMarker>

};

export default AsyncPopupMarker;
